import { useLang } from '@/app/utils/useLang';
import { useRouter } from 'next/router';
import React from 'react';
import { VideoSectionConfig, videoSectionConfigs, VideoSectionKey } from './videoSections';

const getBaseConfig = (key: VideoSectionKey): VideoSectionConfig => {
    return videoSectionConfigs(key);
};

export function getCoverSectionConfig(key: VideoSectionKey) {
    const { moduleText, sectionId, triggerSelector, videoSrc, posterSrc } = getBaseConfig(key);
    const { t } = useLang(`${moduleText}`);
    return {
        label: t('label'),
        title: t('title'),
        desc: t('desc'),
        sectionId,
        triggerSelector,
        videoSrc,
        posterSrc,
    };
}
export function getTabSectionConfig() {
    const { t } = useLang('TabSection');

    const imgs = [
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758540828985-0n0qgs-compressed-Image.png',
            h5: '',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758540856960-lo21f7-compressed-Image.png',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758540869120-7fbsii-compressed-Image.png',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758540879684-hw5wh9-compressed-Image.png',
        },
    ];

    const parseTitle = (title: string) => {
        // 匹配数字和单位
        const numberMatch = title.match(/(\d[\d,. ]*)/);
        if (!numberMatch) return { num: 0, prefix: '', suffix: '' };

        const numStr = numberMatch[1].trim();
        const num = parseFloat(numStr.replace(/[^\d.]/g, ''));

        // 分割前缀和后缀
        const [beforeNum, afterNum] = title.split(numStr);

        return {
            num,
            prefix: beforeNum.trim(),
            suffix: ` ${afterNum?.trim()}` || '',
        };
    };

    return {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1757494640658-map629-compressed-%E5%8B%BF%E7%A7%BB%E5%8A%A8%EF%BC%8C%E6%98%AF%E4%B8%AA%E7%BB%84%E4%BB%B6%E8%83%8C%E6%99%AF%E5%9B%BE.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1757494669531-vu9cys-compressed-PAD/IPHONE%E6%89%8B%E6%9C%BA%E5%85%B1%E7%94%A8%E8%83%8C%E6%99%AF.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1757494669531-vu9cys-compressed-PAD/IPHONE%E6%89%8B%E6%9C%BA%E5%85%B1%E7%94%A8%E8%83%8C%E6%99%AF.jpg',
        list: new Array(4).fill(null).map((item, index) => {
            return {
                title: t(`tab_title${index + 1}`),
                desc: t.rich(`tab_desc${index + 1}`, {
                    sup: (text: string) => <sup>{text}</sup>,
                }),

                img: imgs[index],
            };
        }),
    };
}

export function getCardSectionConfig() {
    const { t } = useLang('CardSection');
    return {
        title: t('title'),
        desc: t.rich('desc', {
            sup: (text: string) => <sup>{text}</sup>,
        }),
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758539157618-835a7f-compressed-Frame%202094654394.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1758539157618-835a7f-compressed-Frame%202094654394.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758539260029-tmaijl-compressed-Frame%202094654409.jpg',
        btnText: t('btnText'),
        moreText: t('moreText'),
    };
}

export function getPhoneSectionConfig() {
    const { t } = useLang('PhoneSection');
    return {
        title: t('title'),
        desc: t('desc'),
        list: [
            { item: t('item1'), pc: 'https://websiteoss.ecoflow.com/figma-uploads/1757491649448-bu45im-compressed-Output.png' },
            { item: t('item2'), pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758765766877-ec2mce-compressed-Solar%20Generation.png' },
            {
                item: t.rich('item3', {
                    sup: (text: string) => <sup>{text}</sup>,
                }),
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1757491689148-0b114n-compressed-Earning.png',
            },
        ],
    };
}

export function getCutCostsConfig() {
    const { t } = useLang('CutCosts');
    return {
        title: t('title'),
        desc: t('desc'),
        list: [
            {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1757493367816-wrlszr-compressed-AI%20Mode.png',
                title: t('tab1_title'),
                desc: t.rich('tab1_desc', {
                    sup: (text: string) => (<sup>{text}</sup>) as React.ReactNode,
                }),
            },
            {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1757493380743-ha729c-compressed-SELF-POWERED.png',
                title: t('tab2_title'),
                desc: t('tab2_desc'),
            },
        ],
    };
}

export function getExploreConfig() {
    const { t } = useLang('Explore');
    const { locale = 'us' } = useRouter();

    // us or ca
    const variantId = locale === 'us' ? '54718699962441' : '62106661618035';

    return {
        title: t('title'),
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758083569694-1tvdi8-compressed-%E9%9B%86%E5%90%88kv.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1758083569694-1tvdi8-compressed-%E9%9B%86%E5%90%88kv.jpg',
        h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758083576443-76aw34-compressed-Frame.jpg',
        list: [
            {
                title: t('item1_title'),
                desc: t('item1_desc'),
                btn: t('item1_btn'),
                url: `https://${locale}.ecoflow.com/products/delta-3-classic-portable-power-station`,
                imgUrl: 'https://websiteoss.ecoflow.com/figma-uploads/1757468563105-oi0qtg-compressed-%E4%BA%A7%E5%93%81%E5%9B%BE.png',
            },
            {
                title: t('item2_title'),
                desc: t('item2_desc'),
                btn: t('item2_btn'),
                url: `https://${locale}.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=${variantId}`,
                imgUrl: 'https://websiteoss.ecoflow.com/figma-uploads/1758264465501-ylf9t8-compressed-%E4%BA%A7%E5%93%81%E5%9B%BE.png',
            },
        ],
    };
}

export function getTcConfig() {
    const { t } = useLang('TC');
    return {
        defaultListLength: 24,
        list: new Array(24).fill(null).map((item, index) => ({
            title: t(`title${index}`),
        })),
    };
}
